import React, { Suspense, useEffect } from "react";
import { Helmet } from "react-helmet";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { Provider } from "react-redux";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga";

// import Navbar from "./components/layout/Navbar";
import { LOGOUT } from "./actions/types";

// Redux
import store from "./store";
import { loadUser } from "./actions/auth";
import setAuthToken from "./utils/setAuthToken";

import Footer from "./components/layout/Footer";
import ChatContainer from "./components/chat/ChatContainer";
import SubMenu from "./components/layout/SubMenu";
import Alert from "./components/layout/Alert";
import { routes } from "./routes";
import ErrorBoundary from "./components/error-boundary/ErrorBoundary";
import {
  NetworkErrorFallback,
  AuthErrorFallback,
  PermissionErrorFallback,
  ChunkErrorFallback,
  ComponentErrorFallback,
  GenericErrorFallback
} from "./components/error-boundary/ErrorFallbackComponents";

import "./App.css";
import './i18n';
import ChatProvider from "./context/ChatProvider";
import { ErrorProvider } from "./context/ErrorContext";
import NavbarTop from "./components/layout/NavbarTop";

ReactGA.initialize('G-KENKSM5XSQ', { debug: true });

const App = () => {
  const { t } = useTranslation();
  useEffect(() => {
    // check for token in LS when app first runs
    if (localStorage.token) {
      // if there is a token set axios headers for all requests
      setAuthToken(localStorage.token);
    }
    // try to fetch a user, if no token or invalid token we
    // will get a 401 response from our API
    // console.log(`store.getState()`, store.getState());
    store.dispatch(loadUser());

    // log user out from all tabs if they log out in one tab
    window.addEventListener("storage", () => {
      if (!localStorage.token) store.dispatch({ type: LOGOUT });
    });
  }, []);

  return (
    <Provider store={store}>
      <ErrorProvider>
        <ErrorBoundary
          fallbackComponent={GenericErrorFallback}
          maxRetries={3}
          showErrorDetails={process.env.NODE_ENV === 'development'}
        >
          <Router>
            <Helmet>
              <title>Welcome - {t("SITE_TITLE_2")}</title>
              <meta name="description" content={t("SITE_DESCRIPTION")} />
            </Helmet>
            <ErrorBoundary
              fallbackComponent={ComponentErrorFallback}
              maxRetries={2}
              showErrorDetails={process.env.NODE_ENV === 'development'}
            >
              <div>
                <NavbarTop />
              </div>
            </ErrorBoundary>
            <ErrorBoundary
              fallbackComponent={ComponentErrorFallback}
              maxRetries={2}
              showErrorDetails={process.env.NODE_ENV === 'development'}
            >
              <SubMenu />
            </ErrorBoundary>
            <div className="container p-5">
              <ErrorBoundary
                fallbackComponent={ComponentErrorFallback}
                maxRetries={2}
                showErrorDetails={process.env.NODE_ENV === 'development'}
              >
                <Alert />
              </ErrorBoundary>
              <ErrorBoundary
                fallbackComponent={ChunkErrorFallback}
                maxRetries={1}
                showErrorDetails={process.env.NODE_ENV === 'development'}
              >
                <Routes>
                  {routes.map(({ path, element }) => (
                    <Route
                      key={path}
                      exact
                      path={path}
                      element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <ErrorBoundary
                            fallbackComponent={({ errorType, ...props }) => {
                              switch (errorType) {
                                case 'network':
                                  return <NetworkErrorFallback {...props} />;
                                case 'auth':
                                  return <AuthErrorFallback {...props} />;
                                case 'permission':
                                  return <PermissionErrorFallback {...props} />;
                                case 'chunk':
                                  return <ChunkErrorFallback {...props} />;
                                case 'component':
                                  return <ComponentErrorFallback {...props} />;
                                default:
                                  return <GenericErrorFallback {...props} />;
                              }
                            }}
                            maxRetries={2}
                            showErrorDetails={process.env.NODE_ENV === 'development'}
                          >
                            {element}
                          </ErrorBoundary>
                        </Suspense>
                      }
                    />
                  ))}
                </Routes>
              </ErrorBoundary>
            </div>
            <ErrorBoundary
              fallbackComponent={ComponentErrorFallback}
              maxRetries={2}
              showErrorDetails={process.env.NODE_ENV === 'development'}
            >
              <div>
                <Footer />
              </div>
            </ErrorBoundary>
            <ErrorBoundary
              fallbackComponent={ComponentErrorFallback}
              maxRetries={2}
              showErrorDetails={process.env.NODE_ENV === 'development'}
            >
              <ChatProvider>
                <ChatContainer />
              </ChatProvider>
            </ErrorBoundary>
          </Router>
        </ErrorBoundary>
      </ErrorProvider>
    </Provider>
  );
};

export default App;
